/**
 * Customer-side mirror of stylon-salon-portal/src/Pages/ShopProducts/productImages.js.
 * Kept in sync by hand (small, stable contract).
 */
import { otherOptionsPhotoUrls } from './productGallery';

export function sortVariantImages(variant) {
  const imgs = variant?.images ?? [];
  if (imgs.length <= 1) return imgs.slice();
  return imgs.slice().sort((a, b) => {
    if (a.isPrimary && !b.isPrimary) return -1;
    if (!a.isPrimary && b.isPrimary) return 1;
    return (a.position ?? 0) - (b.position ?? 0);
  });
}

/**
 * The product's own gallery, in the order the owner arranged it: whatever they
 * marked primary first, then by `position`. Identical to the rule in
 * `utils/productGallery`, and deliberately so — a card and the page it opens
 * must not disagree about which photograph leads.
 */
function orderedProductImages(product) {
  return (Array.isArray(product?.images) ? product.images : [])
    .filter((i) => !!(i && typeof i.url === 'string' && i.url.trim()))
    .map((image, index) => ({ image, index }))
    .sort((a, b) => {
      if (!!b.image.isPrimary !== !!a.image.isPrimary) return b.image.isPrimary ? 1 : -1;
      const pa = a.image.position ?? Number.MAX_SAFE_INTEGER;
      const pb = b.image.position ?? Number.MAX_SAFE_INTEGER;
      // A stable sort by index, so two rows sharing a position keep the order
      // the server sent rather than swapping between engines.
      return pa - pb || a.index - b.index;
    })
    .map((e) => e.image);
}

export function interleaveVariantImages(variants) {
  if (!Array.isArray(variants) || variants.length === 0) return [];
  const usable = variants.filter((v) => v?.isActive !== false);
  const source = usable.length > 0 ? usable : variants;
  const buckets = source.map(sortVariantImages).filter((b) => b.length > 0);
  if (buckets.length === 0) return [];
  const out = [];
  for (const b of buckets) out.push(b[0]);
  for (const b of buckets) {
    for (let i = 1; i < b.length; i++) out.push(b[i]);
  }
  return out;
}

/**
 * THE PHOTOGRAPHS A CARD CAN SHOW.
 *
 *   the variations' own, interleaved, then the PRODUCT's own, deduplicated.
 *
 * Migration review, §2 and root cause 6 — "product photos live in two tables
 * and three screens disagree about which". `product_image` holds a product's
 * gallery; `variant_image` holds the one shot that identifies a variation. A
 * catalogue photographed in our portal fills both; a WooCommerce import fills
 * only the first — dk-store has 2 123 rows on the product and 0 on the
 * variation.
 *
 * Every card in this storefront read `interleaveVariantImages` and nothing
 * else, so on that catalogue the "you may also like" strip was ten grey boxes
 * and the wishlist rows had no pictures either. The server now resolves the
 * fallback for the shapes it serialises, and this is the client's half of the
 * same rule, for the payloads it does not — an older API, a cached bundle, a
 * product with no variation rows at all.
 *
 * ── WHY THE PRODUCT'S GALLERY IS A FALLBACK AND NOT AN APPEND ────────────
 *
 * `productGallery.galleryImages` — the PRODUCT PAGE's rule — concatenates:
 * every photograph must be reachable there, because the second angle is what
 * decides a jewellery-set purchase. A CARD is not a gallery. Appending would
 * put all 2 081 of this merchant's product photographs onto a shop grid that
 * has just been cut to 24 cards a screen, undoing the page-weight work in the
 * name of pictures nobody swipes on a 258px card. So the variations lead when
 * they have anything, and the product answers when they do not.
 *
 * `variants` may be narrowed by the caller — the shop grid previews one colour
 * at a time — and defaults to the product's own. Narrowed, the card leaves out
 * the photos of the colours it is not previewing, by the product page's rule
 * (`productGallery.otherOptionsPhotoUrls`): a card filtered to black used to
 * follow black's photo with brown, light blue and pink.
 */
export function productCardImages(product, variants = undefined) {
  const rows = Array.isArray(variants) ? variants : (product?.variants ?? []);
  const usable = (i) => !!(i && typeof i.url === 'string' && i.url.trim());
  const fromVariants = interleaveVariantImages(rows).filter(usable);
  const source = fromVariants.length > 0
    ? [...fromVariants, ...orderedProductImages(product)]
    : orderedProductImages(product);
  const othersPhotos = Array.isArray(variants) ? otherOptionsPhotoUrls(product, variants) : new Set();
  const seen = new Set();
  const out = [];
  for (const image of source) {
    const key = image.url.trim();
    if (seen.has(key) || othersPhotos.has(key)) continue;
    seen.add(key);
    out.push(image);
  }
  return out;
}

export function filterVariantsByOptions(variants, filters = {}) {
  if (!Array.isArray(variants) || variants.length === 0) return [];
  const norm = (s) => (s == null ? null : String(s).toLowerCase());
  const f = {
    color: norm(filters.color),
    size:  norm(filters.size),
    style: norm(filters.style),
  };
  if (!f.color && !f.size && !f.style) return variants;
  return variants.filter((v) =>
    (!f.color || norm(v.color) === f.color) &&
    (!f.size  || norm(v.size)  === f.size)  &&
    (!f.style || norm(v.style) === f.style),
  );
}

/* The colour names → paint map that lived here (resolveColorPreview) is utils/colourSwatch's now: every swatch the
 * storefront paints reads hex, then name, then a neutral ring, from that one file (Swe Sosetta run 5, round C: S round
 * 3). */

/** True when quick-add should open the variant picker instead of adding immediately. */
export function needsVariantPicker(product) {
  const variants = product?.variants ?? [];
  if (variants.length <= 1) return false;
  const colors = new Set(variants.map((v) => v.color).filter(Boolean));
  const sizes = new Set(variants.map((v) => v.size).filter(Boolean));
  const stylesSet = new Set(variants.map((v) => v.style).filter(Boolean));
  return colors.size > 1 || sizes.size > 1 || stylesSet.size > 1;
}
